import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get_todos, create_todo, update_todo, delete_todo, type Todo } from '@/api/todos'
import { toast } from '@/hooks/use-toast'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { usePermission } from '@/hooks/use-permission'
import { UserPermission } from '@/constants/permissions'

interface QuickTasksProps {
    className?: string
}

export function QuickTasks({ className }: QuickTasksProps) {
    const { t } = useTranslation()
    const [newTask, setNewTask] = useState('')
    const queryClient = useQueryClient()
    const { can } = usePermission()

    // Fetch all todos (backend filters out completed tasks older than 24h)
    const { data, isLoading } = useQuery({
        queryKey: ['todos'],
        queryFn: get_todos,
    })

    const tasks = data || []

    // Create todo mutation
    const createMutation = useMutation({
        mutationFn: create_todo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['todos'] })
            setNewTask('')
            toast({
                title: t('task_added'),
                description: t('task_added_desc'),
            })
        },
        onError: (error) => {
            if (axios.isAxiosError(error) && error.response?.status === 400) {
                toast({
                    title: t('error'),
                    description: t('task_limit_reached'),
                    variant: 'destructive',
                })
            } else {
                toast({
                    title: t('error'),
                    description: t('task_add_failed'),
                    variant: 'destructive',
                })
            }
        },
    })

    // Update todo mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: { is_completed: boolean } }) =>
            update_todo(id, data),
        onMutate: async ({ id, data }) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['todos', 'active'] })

            // Snapshot previous value
            const previousTodos = queryClient.getQueryData(['todos', 'active'])

            // Optimistically update
            queryClient.setQueryData(['todos', 'active'], (old: Todo[] | undefined) => {
                if (!old) return old
                return old.map((task) =>
                    task.id === id ? { ...task, is_completed: data.is_completed } : task
                )
            })

            return { previousTodos }
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousTodos) {
                queryClient.setQueryData(['todos', 'active'], context.previousTodos)
            }
            toast({
                title: t('error'),
                description: t('task_update_failed'),
                variant: 'destructive',
            })
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['todos'] })
        },
    })

    // Delete todo mutation
    const deleteMutation = useMutation({
        mutationFn: delete_todo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['todos'] })
            toast({
                title: t('task_deleted'),
                description: t('task_deleted_desc'),
            })
        },
        onError: () => {
            toast({
                title: t('error'),
                description: t('task_delete_failed'),
                variant: 'destructive',
            })
        },
    })

    const handleToggle = (id: string, currentStatus: boolean) => {
        updateMutation.mutate({ id, data: { is_completed: !currentStatus } })
    }

    const handleAdd = (e: React.FormEvent) => {
        e.preventDefault()
        if (!newTask.trim()) return

        createMutation.mutate({ title: newTask })
    }

    const handleDelete = (id: string) => {
        deleteMutation.mutate(id)
    }

    return (
        <div className={cn(
            "bg-card rounded-xl shadow-sm border border-border p-5",
            className
        )}>
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('quick_tasks')}
            </h3>

            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : tasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                    {t('no_active_tasks')}
                </div>
            ) : (
                <div className="space-y-3">
                    {tasks.map((task) => (
                        <div
                            key={task.id}
                            className={cn(
                                "flex items-start gap-3 p-3 rounded-lg transition-colors group",
                                "bg-muted/50 hover:bg-muted"
                            )}
                        >
                            <Checkbox
                                checked={task.is_completed}
                                onCheckedChange={() => handleToggle(task.id, task.is_completed)}
                                className="mt-0.5 border-border data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
                                disabled={!can(UserPermission.CHANGE_TODO)}
                            />
                            <span className={cn(
                                "text-sm flex-1",
                                task.is_completed
                                    ? "text-muted-foreground line-through"
                                    : "text-foreground"
                            )}>
                                {task.title}
                            </span>
                            {can(UserPermission.DELETE_TODO) && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleDelete(task.id)}
                                    disabled={deleteMutation.isPending}
                                >
                                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Add Task Form */}
            {can(UserPermission.ADD_TODO) && (
                <div className="mt-4 pt-3 border-t border-border">
                    <form onSubmit={handleAdd} className="flex gap-2">
                        <Input
                            value={newTask}
                            onChange={(e) => setNewTask(e.target.value)}
                            placeholder={t('add_new_task_placeholder') || 'أضف مهمة جديدة...'}
                            className="flex-1 text-sm"
                            disabled={createMutation.isPending}
                        />
                        <Button
                            type="submit"
                            size="icon"
                            className="bg-primary hover:bg-primary/90"
                            disabled={createMutation.isPending || !newTask.trim()}
                        >
                            {createMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Plus className="h-4 w-4" />
                            )}
                        </Button>
                    </form>
                </div>
            )}
        </div>
    )
}
